// FIG Event stream (DESIGN_SPEC §11.4.7, §10.9, §12.5; RESTYLE §2). Props: FigProps { ctx, no }.
//   meta(ctx) → { id: "events", label: "Events", count: events, empty: no events }
// Tools: the category legend is the filter (shift-click solos), search (pid:4821, host, path…; "/" focuses, matches
// marked), Hide OS background (default on), Grouped | Sequence, T0 | UTC (key t). The lane chart plots every visible
// event on the run clock with BOOT / RUN / COLLECT. Grouped renders inline in content-visibility chunks (200 rows,
// then "Show N more"); Sequence is virtualized. Rows cited by a behavior tag carry its severity edge (documents only).
import { useMemo, useState } from "react";
import { ActionButton, Check, Empty, FigHead, Search, Seg, figTitleId } from "@/components/ui";
import { useHotkeys } from "@/hooks";
import { count, plural } from "@/lib/format";
import { CATEGORIES, categoryOf, countByCategory } from "@/lib/derive/categories";
import type { FigMeta, FigProps, FoldMode, ReportCtx } from "@/lib/derive/types";
import { foldEvents, namesOf, noiseOf, phasesOf, t0Of, tsOf } from "../derive/fold";
import { LaneChart, laneDomain, type LanePoint } from "./LaneChart";
import { CategoryFilter, StreamScroller, useCategoryFilter, useOpenState, useRowFactory } from "./StreamLive";
import { EvHead, ItemList, buildItems, matchRow, parseQuery, type Clock, type StreamItem } from "./StreamRow";

export function meta(ctx: ReportCtx): FigMeta {
  const n = ctx.stream.snap.events.length;
  return { id: "events", label: "Events", count: n, empty: n === 0 };
}

const CHUNK = 100;
const FIRST = 200;

function saveText(name: string, text: string, type: string) {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const chunked = <T,>(xs: readonly T[], n: number): T[][] => {
  const out: T[][] = [];
  for (let i = 0; i < xs.length; i += n) out.push(xs.slice(i, i + n));
  return out;
};

export default function EventStreamFig({ ctx, no }: FigProps) {
  const { stream, job } = ctx;
  const snap = stream.snap;
  const events = snap.events;
  const live = ctx.mode === "live";
  const noise = noiseOf(ctx);
  const names = namesOf(ctx);
  const t0 = t0Of(ctx);
  const cite = live ? undefined : ctx.cite;
  const [mode, setMode] = useState<FoldMode>(live ? "sequence" : "grouped");
  const [clock, setClock] = useState<Clock>("t0");
  const [hideBg, setHideBg] = useState(true);
  const [query, setQuery] = useState("");
  const [limit, setLimit] = useState(FIRST);
  const filter = useCategoryFilter();
  const open = useOpenState();
  useHotkeys({ t: () => setClock((c) => (c === "t0" ? "utc" : "t0")) });

  const opts = { t0, noise, cite, procName: names, sample: job.filename };
  const rows = useMemo(() => foldEvents(events, { ...opts, mode, hideBackground: hideBg }), [events, snap.version, mode, hideBg, t0, noise, cite, names, job.filename]);
  const bySeq = useMemo(() => new Map(events.map((e) => [e.seq, e])), [events, snap.version]);
  const make = useRowFactory(events, snap.version, opts);

  const q = useMemo(() => parseQuery(query), [query]);
  const shown = useMemo(() => rows.filter((r) => !filter.off.has(r.category) && matchRow(r, q)), [rows, filter.off, q]);
  const items = useMemo(
    () =>
      buildItems(shown, {
        clock,
        expanded: open.expanded,
        details: open.details,
        child: (seq) => {
          const e = bySeq.get(seq);
          return e ? make(e) : undefined;
        },
        event: (seq) => bySeq.get(seq),
      }),
    [shown, clock, open.expanded, open.details, bySeq],
  );

  const visible = useMemo(() => (hideBg ? events.filter((e) => !noise.hits.has(e.seq)) : events), [events, snap.version, hideBg, noise]);
  const counts = useMemo(() => countByCategory(visible), [visible]);
  const points = useMemo<LanePoint[]>(() => {
    const base = t0 ?? 0;
    let at = base;
    return visible.map((e) => {
      at = tsOf(e, at);
      return { t: at - base, c: categoryOf(e.kind) };
    });
  }, [visible, t0]);
  const phases = useMemo(() => phasesOf(ctx), [stream.transitions, t0]);
  // The run's whole span, whatever the filters hide, so toggling background never rescales the axis.
  const domain = useMemo(() => {
    const base = t0 ?? 0;
    const ends: LanePoint[] = events.length ? [events[0], events[events.length - 1]].map((e) => ({ t: tsOf(e, base) - base, c: "system" })) : [];
    return laneDomain(ends, phases);
  }, [events, snap.version, t0, phases]);
  const active = useMemo(() => new Set(CATEGORIES.filter((c) => !filter.off.has(c))), [filter.off]);

  const n = events.length;
  const bg = noise.total;
  const filtered = filter.off.size > 0 || q.text !== "";
  const clear = () => {
    filter.all();
    setQuery("");
  };
  const what = [filter.off.size ? CATEGORIES.filter((c) => !filter.off.has(c)).join(" + ") || "no category" : "", q.text ? `“${q.text}”` : ""].filter(Boolean).join(" + ");

  const empty = (
    <Empty inline rule={false} className="px-2.5">
      {filtered ? (
        <>
          No events match {what}.{" "}
          <button type="button" className="lnk" onClick={clear}>
            Clear filters
          </button>
        </>
      ) : hideBg && bg ? (
`Only OS background left · ${count(bg)} hidden`
      ) : (
        "No events"
      )}
    </Empty>
  );

  const first = events[0]?.seq;
  const last = events[events.length - 1]?.seq;
  const head = stream.ended ? "End of stream" : stream.live ? "Live" : "Stream stopped early";
  const rowsShown = items.filter((i): i is Extract<StreamItem, { type: "row" }> => i.type === "row" && !i.child).length;

  return (
    <section className="cell fig" aria-labelledby={figTitleId(no)}>
      <FigHead
        no={no}
        label="Event stream"
        count={`${count(n)} ${plural(n, "event")} · ${count(rowsShown)} ${plural(rowsShown, "row")}`}
        actions={
          <>
            <Seg
              label="Rows"
              value={mode}
              onChange={(m) => {
                setMode(m);
                setLimit(FIRST);
              }}
              options={[
                { value: "grouped", label: "Grouped", title: "One row per signature across the run" },
                { value: "sequence", label: "Sequence", title: "In order; only consecutive repeats fold" },
              ]}
            />
            <Seg
              label="Time"
              value={clock}
              onChange={setClock}
              options={[
                { value: "t0", label: "T0", title: "Relative to T0 (key t)" },
                { value: "utc", label: "UTC", title: "Wall clock, UTC (key t)" },
              ]}
            />
          </>
        }
      />
      <div className="fig-tools">
        <CategoryFilter counts={counts} total={visible.length} off={filter.off} onToggle={filter.toggle} onAll={filter.all} />
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <Search value={query} onChange={setQuery} label="Filter events" placeholder="pid:4821, host, path…" width="min(260px, 100%)" />
          <Check checked={hideBg} onChange={setHideBg} count={count(bg)}>
            Hide OS background
          </Check>
        </div>
      </div>
      <LaneChart points={points} counts={counts} active={active} phases={phases} domain={domain} className="mt-3" />
      {filtered && (
        <p className="filterline">
          <span>
            <b>{count(rowsShown)}</b> of {count(rows.length)} {plural(rows.length, "row")} · {what}
          </span>
          <button type="button" className="lnk" onClick={clear}>
            Clear filters
          </button>
        </p>
      )}
      <div className="stream mt-1" role="log" aria-live="off" aria-label="Event stream">
        {mode === "grouped" ? (
          <>
            <EvHead clock={clock} className="top-[calc(var(--nav-h)+var(--bar-h)+1px)]" />
            {items.length === 0 && empty}
            {chunked(items.slice(0, limit), CHUNK).map((chunk, i) => (
              <div key={i} className="[contain-intrinsic-size:auto_2400px] [content-visibility:auto]">
                <ItemList items={chunk} live={live} terms={q.terms} onToggle={open.toggle} onDetail={open.detail} />
              </div>
            ))}
            {items.length > limit && (
              <button
                type="button"
                className="block w-full border-b border-rule-row px-2.5 py-2 text-left font-mono text-meta text-ink-soft hover:bg-wash hover:text-ink"
                onClick={() => setLimit(Infinity)}
              >
                Show {count(items.length - limit)} more {plural(items.length - limit, "row")}
              </button>
            )}
          </>
        ) : (
          <StreamScroller items={items} live={live} terms={q.terms} clock={clock} onToggle={open.toggle} onDetail={open.detail} empty={empty} />
        )}
        <div className="stream-foot">
          <span>
            <b>{head}</b> · {count(n)} {plural(n, "event")}
            {first !== undefined && last !== undefined && ` · seq ${first}–${last}`}
            {hideBg && bg > 0 && ` · ${count(bg)} background hidden`}
            {!stream.ended && !stream.live && stream.recordState && ` · the record says ${stream.recordState}`}
          </span>
          <ActionButton onClick={() => saveText(`${job.id}.events.jsonl`, events.map((e) => JSON.stringify(e)).join("\n") + "\n", "application/x-ndjson")}>Raw JSONL</ActionButton>
        </div>
      </div>
    </section>
  );
}
